// Validation functions for reports

// Helper function to check if a field has actual text content (strips HTML tags and entities)
const hasTextContent = (value) => {
  if (!value || typeof value !== 'string') return false;
  // Remove HTML tags and common entities, then check if there's actual text
  const textOnly = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').replace(/&lt;/g, '').replace(/&gt;/g, '').trim();
  return textOnly.length > 0;
};

export const reportValidation = {
  /**
   * Validate required fields for report generation
   */
  validateRequiredFields: (reportData, selectedPeriod, selectedSite) => {
    const errors = [];
    
    if (!reportData.title || !reportData.title.trim()) {
      errors.push('Report title is required');
    }
    
    if (!selectedPeriod.startDate) {
      errors.push('Start date is required');
    }
    
    if (!selectedPeriod.endDate) {
      errors.push('End date is required');
    }
    
    if (!selectedSite) {
      errors.push('Site selection is required');
    }
    
    // Validate date range
    if (selectedPeriod.startDate && selectedPeriod.endDate) {
      const startDate = new Date(selectedPeriod.startDate);
      const endDate = new Date(selectedPeriod.endDate);
      
      if (startDate > endDate) {
        errors.push('Start date cannot be after end date');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  },

  /**
   * Calculate completion status for individual tabs
   */
  validateTabCompletion: (tabId, reportData, reportSections, selectedPeriod, selectedSite, selectedPhotos, photoLabels, importedPhotos) => {
    // Debug logging to check if validation runs on content deletion
    console.log(`🔍 Validating tab: ${tabId}`, { reportSections: !!reportSections });
    
    switch (tabId) {
      case 'cover-page':
        const hasDateRange = selectedPeriod.startDate && selectedPeriod.endDate;
        const hasReportTitle = reportData.title && reportData.title.trim();
        const hasAuthor = reportData.author && reportData.author.trim();
        const hasSignature = reportData.signature && reportData.signature.trim();
        
        const coverFieldsCount = [hasReportTitle, hasAuthor, hasSignature].filter(Boolean).length;
        
        if (!hasDateRange && coverFieldsCount === 0) return 'empty';
        if (hasDateRange && coverFieldsCount >= 2) return 'complete';
        return 'partial';
      
      case 'contents':
        return 'complete'; // Auto-generated, always complete
      
      case 'photos':
        // Check importedPhotos (Section 1.0 Progress Photos)
        if (!importedPhotos || importedPhotos.length === 0) return 'empty';
        return 'complete'; // Any photos = complete
      
      case 'labels':
        if (!selectedPhotos || selectedPhotos.length === 0) return 'empty';
        const labelledPhotos = selectedPhotos.filter(photoId => 
          photoLabels[photoId]?.label && photoLabels[photoId]?.description
        ).length;
        if (labelledPhotos === 0) return 'empty';
        return labelledPhotos === selectedPhotos.length ? 'complete' : 'partial';
      
      case 'executive-summary':
        const execSection = reportSections?.executiveSummary;
        if (!execSection) return 'empty';
        
        const execFields = ['projectOverview', 'keyMetrics', 'majorAchievements', 'challengesIssues', 'nextSteps'];
        const filledExecFields = execFields.filter(field => hasTextContent(execSection[field])).length;
        
        if (filledExecFields === 0) return 'empty';
        return filledExecFields === execFields.length ? 'complete' : 'partial';
      
      case 'project-overview':
        const projSection = reportSections?.projectOverview;
        if (!projSection) return 'empty';
        
        const projFields = ['projectDescription', 'scope', 'objectives', 'timeline', 'stakeholders', 'constraints', 'qualityStandards'];
        const filledProjFields = projFields.filter(field => hasTextContent(projSection[field])).length;
        
        if (filledProjFields === 0) return 'empty';
        return filledProjFields === projFields.length ? 'complete' : 'partial';
      
      case 'progress-highlights':
        const progressSection = reportSections?.progressHighlights;
        if (!progressSection) return 'empty';
        
        // Check textbox fields
        const progressTextboxes = ['majorMilestones', 'notableAchievements', 'performanceImprovements', 'teamRecognition'];
        const filledProgressTextboxes = progressTextboxes.filter(field => {
          const value = progressSection[field];
          return value && typeof value === 'string' && value.trim().length > 0;
        }).length;
        
        // Check KPI sliders (more strict - must have meaningful values)
        const kpiFields = ['overallCompletion', 'schedulePerformance', 'budgetPerformance', 'qualityScore', 'safetyScore'];
        const filledKPIs = kpiFields.filter(field => {
          const value = progressSection[field];
          return value !== undefined && value !== null && value !== '' && value !== 0;
        }).length;
        
        const totalProgressFields = progressTextboxes.length + kpiFields.length;
        const filledProgressFields = filledProgressTextboxes + filledKPIs;
        
        if (filledProgressFields === 0) return 'empty';
        return filledProgressFields === totalProgressFields ? 'complete' : 'partial';
      
      case 'work-completed':
        const workSection = reportSections?.workCompleted;
        if (!workSection) return 'empty';
        
        const workFields = ['structuralWork', 'mepSystems', 'architecturalWork', 'siteWork', 'testingCommissioning'];
        const filledWorkFields = workFields.filter(field => hasTextContent(workSection[field])).length;
        
        if (filledWorkFields === 0) return 'empty';
        return filledWorkFields === workFields.length ? 'complete' : 'partial';
      
      case 'quality-inspections':
        const qualitySection = reportSections?.qualityInspections;
        if (!qualitySection) return 'empty';
        
        // Check textbox fields
        const qualityTextboxes = ['overview', 'inspectionDetails', 'standardsCompliance', 'nonConformances', 'materialTesting', 'qualityImprovements'];
        const filledQualityTextboxes = qualityTextboxes.filter(field => qualitySection[field]?.trim()).length;
        
        // Check metrics
        const qualityMetrics = ['totalInspections', 'inspectionsPassed', 'inspectionsFailed', 'successRate'];
        const filledQualityMetrics = qualityMetrics.filter(field => qualitySection[field]?.toString().trim()).length;
        
        const totalQualityFields = qualityTextboxes.length + qualityMetrics.length;
        const filledQualityFields = filledQualityTextboxes + filledQualityMetrics;
        
        if (filledQualityFields === 0) return 'empty';
        return filledQualityFields === totalQualityFields ? 'complete' : 'partial';
      
      case 'safety-compliance':
        const safetySection = reportSections?.safetyCompliance;
        if (!safetySection) return 'empty';
        
        // Check textbox fields
        const safetyTextboxes = ['incidentReports', 'complianceStatus', 'safetyInspections', 'ppeCompliance', 'safetyTraining', 'safetyImprovements'];
        const filledSafetyTextboxes = safetyTextboxes.filter(field => safetySection[field]?.trim()).length;
        
        // Check metrics
        const safetyMetrics = ['daysWithoutIncident', 'totalLaborHours', 'nearMisses', 'trainingHours'];
        const filledSafetyMetrics = safetyMetrics.filter(field => safetySection[field]?.toString().trim()).length;
        
        const totalSafetyFields = safetyTextboxes.length + safetyMetrics.length;
        const filledSafetyFields = filledSafetyTextboxes + filledSafetyMetrics;
        
        if (filledSafetyFields === 0) return 'empty';
        return filledSafetyFields === totalSafetyFields ? 'complete' : 'partial';
      
      case 'schedule-performance':
        const scheduleSection = reportSections?.schedulePerformance;
        if (!scheduleSection) return 'empty';
        
        // Check textbox fields
        const scheduleTextboxes = ['criticalPath', 'scheduleVariances', 'milestonePerformance', 'resourceImpact', 'recoveryStrategies', 'cashflowImpact'];
        const filledScheduleTextboxes = scheduleTextboxes.filter(field => scheduleSection[field]?.trim()).length;
        
        // Check metrics
        const scheduleMetrics = ['originalDuration', 'revisedDuration', 'spi'];
        const filledScheduleMetrics = scheduleMetrics.filter(field => scheduleSection[field]?.toString().trim()).length;
        
        const totalScheduleFields = scheduleTextboxes.length + scheduleMetrics.length;
        const filledScheduleFields = filledScheduleTextboxes + filledScheduleMetrics;
        
        if (filledScheduleFields === 0) return 'empty';
        return filledScheduleFields === totalScheduleFields ? 'complete' : 'partial';
      
      case 'resource-utilization':
        const resourceSection = reportSections?.resourceUtilization;
        if (!resourceSection) return 'empty';
        
        // Check textbox fields
        const resourceTextboxes = ['laborAnalysis', 'equipmentAnalysis', 'materialManagement', 'optimizationStrategies', 'performanceBenchmarks'];
        const filledResourceTextboxes = resourceTextboxes.filter(field => resourceSection[field]?.trim()).length;
        
        // Check table data
        const resourceTables = ['labour', 'materialsOnSite', 'plantOnSite', 'materialsNeeded', 'plantNeeded'];
        const filledResourceTables = resourceTables.filter(table => {
          const tableData = resourceSection[table];
          return Array.isArray(tableData) && tableData.length > 0 && 
                 tableData.some(row => Object.values(row).some(val => val?.toString().trim()));
        }).length;
        
        const totalResourceFields = resourceTextboxes.length + resourceTables.length;
        const filledResourceFields = filledResourceTextboxes + filledResourceTables;
        
        if (filledResourceFields === 0) return 'empty';
        return filledResourceFields === totalResourceFields ? 'complete' : 'partial';
      
      case 'budget-analysis':
        const budgetSection = reportSections?.budgetAnalysis;
        if (!budgetSection) return 'empty';
        
        // Check textbox fields
        const budgetTextboxes = ['varianceAnalysis', 'changeOrders', 'cashFlow', 'costForecast'];
        const filledBudgetTextboxes = budgetTextboxes.filter(field => budgetSection[field]?.trim()).length;
        
        // Check metrics
        const budgetMetrics = ['originalBudget', 'revisedBudget', 'laborCosts', 'materialCosts', 'equipmentCosts', 'overheadCosts'];
        const filledBudgetMetrics = budgetMetrics.filter(field => budgetSection[field]?.toString().trim()).length;
        
        const totalBudgetFields = budgetTextboxes.length + budgetMetrics.length;
        const filledBudgetFields = filledBudgetTextboxes + filledBudgetMetrics;
        
        if (filledBudgetFields === 0) return 'empty';
        return filledBudgetFields === totalBudgetFields ? 'complete' : 'partial';
      
      case 'risk-assessment':
        const riskSection = reportSections?.riskAssessment;
        if (!riskSection) return 'empty';
        
        // Check textbox fields (9 textboxes)
        const riskTextboxes = ['technicalDesignRisks', 'financialCommercialRisks', 'environmentalExternalRisks', 'operationalResourceRisks', 'otherRisks', 'highPriorityAnalysis', 'mitigationStrategies', 'contingencyPlans', 'riskMonitoring'];
        const filledRiskTextboxes = riskTextboxes.filter(field => riskSection[field]?.trim()).length;
        
        // Check metrics (8 metrics)
        const riskMetrics = ['totalRisks', 'highPriorityRisks', 'risksMitigated', 'residualRiskIndex', 'technicalRisks', 'financialRisks', 'environmentalRisks', 'operationalRisks'];
        const filledRiskMetrics = riskMetrics.filter(field => riskSection[field]?.toString().trim()).length;
        
        const totalRiskFields = riskTextboxes.length + riskMetrics.length;
        const filledRiskFields = filledRiskTextboxes + filledRiskMetrics;
        
        if (filledRiskFields === 0) return 'empty';
        return filledRiskFields === totalRiskFields ? 'complete' : 'partial';
      
      case 'progress-comparisons':
        const progressCompSection = reportSections?.progressComparisons;
        if (!progressCompSection?.photoGrids) return 'empty';
        
        const grids = progressCompSection.photoGrids;
        if (grids.length === 0) return 'empty';
        
        // Check if grids have content (photos OR labels)
        const gridsWithContent = grids.filter(grid => 
          grid.title?.trim() ||
          grid.leftPhoto?.url || 
          grid.rightPhoto?.url || 
          grid.leftPhoto?.label?.trim() || 
          grid.rightPhoto?.label?.trim()
        ).length;
        
        if (gridsWithContent === 0) return 'empty';
        return gridsWithContent === grids.length ? 'complete' : 'partial';
      
      case 'technical-drawings':
        const techSection = reportSections?.technicalDrawings;
        if (!techSection) return 'empty';
        
        // Check metrics (3 metrics)
        const techMetrics = ['totalDrawings', 'revisions', 'completedDrawings'];
        const filledTechMetrics = techMetrics.filter(field => techSection[field]?.toString().trim()).length;
        
        // Check default table data (4 tables)
        const techTables = ['architectural', 'structural', 'mep', 'other'];
        const filledTechTables = techTables.filter(table => {
          const tableData = techSection[table];
          return Array.isArray(tableData) && tableData.length > 0 && 
                 tableData.some(row => Object.values(row).some(val => val?.toString().trim()));
        }).length;
        
        // Check custom tables
        const customTables = techSection.customTables || [];
        const filledCustomTables = customTables.filter(table => 
          table.rows && table.rows.length > 0 &&
          table.rows.some(row => Object.values(row).some(val => val?.toString().trim()))
        ).length;
        
        const totalTechFields = techMetrics.length + techTables.length;
        const filledTechFields = filledTechMetrics + filledTechTables + filledCustomTables;
        
        if (filledTechFields === 0) return 'empty';
        return filledTechFields >= totalTechFields ? 'complete' : 'partial';
      
      case 'next-phase':
        const nextPhaseSection = reportSections?.nextPhase;
        if (!nextPhaseSection) return 'empty';
        
        // Check textbox fields (6 textboxes)
        const phaseTextboxes = ['scopeOfWork', 'keyMilestones', 'riskManagement', 'qualitySafety', 'financialDetails', 'coordination'];
        const filledPhaseTextboxes = phaseTextboxes.filter(field => nextPhaseSection[field]?.trim()).length;
        
        // Check metrics (3 metrics)
        const phaseMetrics = ['phaseName', 'startDate', 'duration'];
        const filledPhaseMetrics = phaseMetrics.filter(field => nextPhaseSection[field]?.toString().trim()).length;
        
        const totalPhaseFields = phaseTextboxes.length + phaseMetrics.length;
        const filledPhaseFields = filledPhaseTextboxes + filledPhaseMetrics;
        
        if (filledPhaseFields === 0) return 'empty';
        return filledPhaseFields === totalPhaseFields ? 'complete' : 'partial';
      
      case 'appendices':
        // Section 15.0 is intentionally blank
        return 'empty';
      
      default:
        return 'empty';
    }
  },

  /**
   * Get completion indicator component data
   */
  getCompletionIndicator: (status) => {
    switch (status) {
      case 'complete':
        return { className: 'w-2 h-2 rounded-full bg-green-500 flex-shrink-0', label: 'Complete' };
      case 'partial':
        return { className: 'w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0', label: 'Partial' };
      case 'empty':
      default:
        return { className: 'w-2 h-2 rounded-full bg-slate-500 flex-shrink-0', label: 'Empty' };
    }
  },

  /**
   * Validate photo selection and labeling
   */
  validatePhotos: (selectedPhotos, photoLabels) => {
    const validation = {
      hasPhotos: selectedPhotos && selectedPhotos.length > 0,
      photosCount: selectedPhotos ? selectedPhotos.length : 0,
      labelledCount: 0,
      unlabelledPhotos: []
    };

    if (selectedPhotos) {
      validation.labelledCount = selectedPhotos.filter(photoId => 
        photoLabels[photoId]?.label && photoLabels[photoId]?.description
      ).length;
      
      validation.unlabelledPhotos = selectedPhotos.filter(photoId => 
        !photoLabels[photoId]?.label || !photoLabels[photoId]?.description
      );
    }

    return validation;
  }
};